/* LaborCostMonitorDialog — the "Live Labor Cost Monitor" screen.
   A tile per hotel department; a tile GLOWS RED when its labor cost
   exceeds budget (weekly or monthly). Drill into a department to enter
   the budget forecast, manage employees + hourly rates, see the live
   cost-vs-budget calc, and request an AI advisory. */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, RefreshCw, Plus, Trash2, Sparkles, Loader2, AlertTriangle, ArrowLeft, Download } from 'lucide-react';
import { isAdminLike } from '../lib/permissions';
import LaborRatioPanel from './LaborRatioPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n, cur) => `${cur || 'USD'} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function LaborCostMonitorDialog({ open, onClose, settings }) {
  const [summary, setSummary] = useState({ departments: [], alarm_count: 0 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);      // open department detail
  const [savingBudget, setSavingBudget] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [tab, setTab] = useState('cost'); // 'cost' | 'ratio'
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const canEdit = isAdminLike(typeof window !== 'undefined' ? localStorage.getItem('user_role') : null)
    || (typeof window !== 'undefined' && localStorage.getItem('user_role') === 'editor');
  const auth = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  const primary = settings?.primary_color || '#1F4FBE';

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/labor-cost/summary`, auth);
      setSummary(data);
    } catch { /* keep */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setDetail(null); setAnalysis(null);
    loadSummary();
  }, [open, loadSummary]);

  const openDetail = async (id) => {
    setAnalysis(null);
    try {
      const { data } = await axios.get(`${API}/labor-cost/${id}`, auth);
      setDetail(data);
    } catch { toast.error('Could not open department.'); }
  };

  const updateEmp = (idx, patch) => setDetail((d) => ({ ...d, employees: d.employees.map((e, i) => i === idx ? { ...e, ...patch } : e) }));
  const addEmp = () => setDetail((d) => ({ ...d, employees: [...(d.employees || []), { name: '', position: '', pay_rate: '', contracted_hours: 40 }] }));
  const removeEmp = (idx) => setDetail((d) => ({ ...d, employees: d.employees.filter((_, i) => i !== idx) }));

  const saveAll = async () => {
    setSavingBudget(true);
    try {
      await axios.put(`${API}/admin/labor-cost/${detail.id}/employees`, { employees: detail.employees }, auth);
      const { data } = await axios.put(`${API}/admin/labor-cost/${detail.id}/budget`, detail.budget, auth);
      setDetail((d) => ({ ...d, computed: data.computed }));
      toast.success('Saved. Cost recalculated.');
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSavingBudget(false); }
  };

  const pullFromMaster = async () => {
    setPulling(true);
    try {
      const { data } = await axios.get(`${API}/labor-cost/${detail.id}/master-budget-suggestion`, auth);
      if (!data.found) {
        toast.info('No matching entry found in the master property budget for this department.');
        return;
      }
      setDetail((d) => ({ ...d, budget: { ...d.budget, currency: data.currency || d.budget.currency, weekly_forecast: data.weekly_forecast, monthly_forecast: data.monthly_forecast } }));
      toast.success(`Pulled from "${data.source_title}" (amount treated as monthly). Review & Save.`);
    } catch {
      toast.error('Could not read the master budget.');
    } finally {
      setPulling(false);
    }
  };

  const runAnalysis = async () => {    setAnalyzing(true); setAnalysis(null);
    try {
      const { data } = await axios.post(`${API}/admin/labor-cost/${detail.id}/analyze`, {}, auth);
      setAnalysis(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI analysis failed.');
    } finally { setAnalyzing(false); }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex flex-col" style={{ backgroundColor: '#0A1A2F', fontFamily: 'Manrope' }} data-testid="labor-cost-monitor">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,.1)' }}>
        <div className="flex items-center gap-3">
          {detail && <button onClick={() => { setDetail(null); setAnalysis(null); }} className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10" data-testid="labor-back"><ArrowLeft className="w-5 h-5" /></button>}
          <div className="w-2.5 h-2.5 rounded-full bg-blue-400 animate-pulse" />
          <h2 className="text-white text-lg font-bold">{detail ? detail.name : 'Live Labor Cost Monitor'}</h2>
          {!detail && tab === 'cost' && summary.alarm_count > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white" data-testid="labor-alarm-count">
              <AlertTriangle className="w-3.5 h-3.5" /> {summary.alarm_count} over budget
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!detail && <button onClick={loadSummary} className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10" data-testid="labor-refresh"><RefreshCw className="w-4 h-4" /></button>}
          <button onClick={onClose} className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10" data-testid="labor-close"><X className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-5">
        {!detail && (
          <div className="max-w-6xl mx-auto mb-4 flex gap-2" data-testid="labor-tabs">
            <button onClick={() => setTab('cost')} data-testid="labor-tab-cost"
              className={`px-4 py-2 rounded-full text-xs font-bold ${tab === 'cost' ? 'bg-white text-[#0A1A2F]' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
              Labor Cost
            </button>
            <button onClick={() => setTab('ratio')} data-testid="labor-tab-ratio"
              className={`px-4 py-2 rounded-full text-xs font-bold ${tab === 'ratio' ? 'bg-white text-[#0A1A2F]' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
              FTA Ratio
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-full text-white/60"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : (!detail && tab === 'ratio') ? (
          <LaborRatioPanel settings={settings} />
        ) : !detail ? (
          /* Department grid */
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {summary.departments.map((d) => {
              const over = d.over_budget;
              const hasBudget = d.weekly_budget > 0 || d.monthly_budget > 0;
              const pct = d.weekly_pct ?? d.monthly_pct;
              return (
                <button key={d.id} onClick={() => openDetail(d.id)} data-testid={`labor-dept-${d.id}`}
                  className={`text-left rounded-xl p-4 transition-all hover:-translate-y-0.5 ${over ? 'labor-glow-red' : ''}`}
                  style={{ backgroundColor: over ? '#3B0D0D' : 'rgba(255,255,255,.06)', border: `1px solid ${over ? '#EF4444' : 'rgba(255,255,255,.12)'}` }}>
                  <p className="text-white font-bold text-sm">{d.name}</p>
                  <p className="text-2xl font-extrabold mt-1" style={{ color: over ? '#FCA5A5' : '#fff' }}>{money(d.weekly_cost, d.currency)}<span className="text-xs font-semibold text-white/50">/wk</span></p>
                  {hasBudget ? (
                    <p className="text-xs mt-1 font-semibold" style={{ color: over ? '#FCA5A5' : '#A3D4D4' }}>
                      {pct != null ? `${pct}% of budget` : ''} {over ? '· OVER' : ''}
                    </p>
                  ) : (
                    <p className="text-xs mt-1 text-white/40">Budget not set</p>
                  )}
                  <p className="text-[11px] text-white/40 mt-1">{d.employee_count} staff</p>
                  {over && <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold text-red-300"><AlertTriangle className="w-3 h-3" /> Attention</span>}
                </button>
              );
            })}
          </div>
        ) : (
          /* Department detail */
          <div className="max-w-3xl mx-auto space-y-5">
            {/* Cost summary */}
            <div className="rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3" style={{ backgroundColor: detail.computed.over_budget ? '#3B0D0D' : 'rgba(255,255,255,.06)', border: `1px solid ${detail.computed.over_budget ? '#EF4444' : 'rgba(255,255,255,.12)'}` }} data-testid="labor-detail-summary">
              <Stat label="Weekly cost" value={money(detail.computed.weekly_cost, detail.computed.currency)} over={detail.computed.weekly_over} />
              <Stat label="Weekly budget" value={detail.computed.weekly_budget ? money(detail.computed.weekly_budget, detail.computed.currency) : '—'} />
              <Stat label="Monthly cost" value={money(detail.computed.monthly_cost, detail.computed.currency)} over={detail.computed.monthly_over} />
              <Stat label="Monthly budget" value={detail.computed.monthly_budget ? money(detail.computed.monthly_budget, detail.computed.currency) : '—'} />
            </div>

            {/* Budget entry */}
            <div className="rounded-xl p-4 bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/80 font-bold text-sm">Budget forecast (from master budget — editable)</p>
                {canEdit && (
                  <button onClick={pullFromMaster} disabled={pulling} className="flex items-center gap-1.5 text-xs font-bold text-[#A3D4D4] hover:text-white disabled:opacity-50" data-testid="labor-pull-master">
                    {pulling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Pull from master budget
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <LabeledInput label="Currency">
                  <select value={detail.budget.currency} onChange={(e) => setDetail((d) => ({ ...d, budget: { ...d.budget, currency: e.target.value } }))} disabled={!canEdit} className="w-full px-2 py-2 rounded-lg text-sm font-bold" data-testid="labor-currency">
                    <option value="USD">USD</option><option value="JMD">JMD</option>
                  </select>
                </LabeledInput>
                <LabeledInput label="Weekly forecast">
                  <input type="number" min={0} value={detail.budget.weekly_forecast} onChange={(e) => setDetail((d) => ({ ...d, budget: { ...d.budget, weekly_forecast: e.target.value } }))} disabled={!canEdit} className="w-full px-2 py-2 rounded-lg text-sm" data-testid="labor-weekly-budget" />
                </LabeledInput>
                <LabeledInput label="Monthly forecast">
                  <input type="number" min={0} value={detail.budget.monthly_forecast} onChange={(e) => setDetail((d) => ({ ...d, budget: { ...d.budget, monthly_forecast: e.target.value } }))} disabled={!canEdit} className="w-full px-2 py-2 rounded-lg text-sm" data-testid="labor-monthly-budget" />
                </LabeledInput>
              </div>
            </div>

            {/* Employees */}
            <div className="rounded-xl p-4 bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/80 font-bold text-sm">Employees &amp; hourly rates</p>
                {canEdit && <button onClick={addEmp} className="flex items-center gap-1 text-xs font-bold text-white/80 hover:text-white" data-testid="labor-add-emp"><Plus className="w-3.5 h-3.5" /> Add</button>}
              </div>
              <div className="space-y-1.5">
                {(detail.employees || []).map((e, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <input value={e.name} onChange={(ev) => updateEmp(i, { name: ev.target.value })} disabled={!canEdit} placeholder="Name" className="col-span-4 px-2 py-1.5 rounded text-xs" data-testid={`labor-emp-name-${i}`} />
                    <input value={e.position || ''} onChange={(ev) => updateEmp(i, { position: ev.target.value })} disabled={!canEdit} placeholder="Position" className="col-span-3 px-2 py-1.5 rounded text-xs" />
                    <input type="number" min={0} step="0.01" value={e.pay_rate ?? ''} onChange={(ev) => updateEmp(i, { pay_rate: ev.target.value })} disabled={!canEdit} placeholder="Rate/hr" className="col-span-2 px-2 py-1.5 rounded text-xs text-right" data-testid={`labor-emp-rate-${i}`} />
                    <input type="number" min={0} value={e.contracted_hours ?? ''} onChange={(ev) => updateEmp(i, { contracted_hours: ev.target.value })} disabled={!canEdit} placeholder="Hrs/wk" className="col-span-2 px-2 py-1.5 rounded text-xs text-right" />
                    {canEdit && <button onClick={() => removeEmp(i)} className="col-span-1 justify-self-center p-1 rounded hover:bg-red-500/20"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>}
                  </div>
                ))}
                {(detail.employees || []).length === 0 && <p className="text-white/40 text-xs">No employees yet. Add staff with their hourly rate to compute labor cost.</p>}
              </div>
            </div>

            {canEdit && (
              <div className="flex gap-2">
                <button onClick={saveAll} disabled={savingBudget} className="flex-1 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-50" style={{ backgroundColor: primary }} data-testid="labor-save">
                  {savingBudget ? 'Saving…' : 'Save & recalculate'}
                </button>
                <button onClick={runAnalysis} disabled={analyzing} className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm bg-white/10 text-white hover:bg-white/20 disabled:opacity-50" data-testid="labor-analyze">
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI advisory
                </button>
              </div>
            )}

            {analysis && (
              <div className="rounded-xl p-4 bg-white/5 border border-white/10" data-testid="labor-analysis">
                <p className="text-white font-bold text-sm mb-1">AI advisory <span className="text-xs font-semibold" style={{ color: analysis.status === 'over' ? '#FCA5A5' : '#A3D4D4' }}>· {analysis.status}</span></p>
                <p className="text-white/80 text-sm mb-2">{analysis.assessment}</p>
                {(analysis.recommendations || []).length > 0 && (
                  <ul className="list-disc pl-5 space-y-1">
                    {analysis.recommendations.map((r, i) => <li key={i} className="text-white/70 text-xs">{r}</li>)}
                  </ul>
                )}
                {analysis.risk_note && <p className="text-amber-300 text-xs mt-2">⚠ {analysis.risk_note}</p>}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .labor-glow-red { animation: labor-pulse 1.6s ease-in-out infinite; }
        @keyframes labor-pulse {
          0%,100% { box-shadow: 0 0 0 rgba(239,68,68,0); }
          50% { box-shadow: 0 0 22px rgba(239,68,68,0.65); }
        }
        [data-testid="labor-cost-monitor"] input, [data-testid="labor-cost-monitor"] select { background:#fff; color:#0A1A2F; border:1px solid #D6D0C0; outline:none; }
        [data-testid="labor-cost-monitor"] input:disabled, [data-testid="labor-cost-monitor"] select:disabled { background:#F1EFEA; color:#6B7280; }
      `}</style>
    </div>,
    document.body
  );
}

function Stat({ label, value, over }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-white/50">{label}</p>
      <p className="text-sm font-extrabold mt-0.5" style={{ color: over ? '#FCA5A5' : '#fff' }}>{value}</p>
    </div>
  );
}

function LabeledInput({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 block mb-1">{label}</label>
      {children}
    </div>
  );
}
